import jsPDF from 'jspdf';
import { Estimate, EstimateStatus } from '../types/estimate';

const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
};

const formatCurrency = (amount: number) => `BD ${amount.toFixed(3)}`;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

// Soft badge colors (bg/text), mirroring the app's own ESTIMATE_STATUS_COLORS.
const STATUS_BADGE: Record<EstimateStatus, { bg: [number, number, number]; text: [number, number, number] }> = {
  DRAFT:    { bg: [254, 243, 199], text: [146, 64, 14] },
  SENT:     { bg: [219, 234, 254], text: [30, 64, 175] },
  ACCEPTED: { bg: [220, 252, 231], text: [22, 101, 52] },
  DECLINED: { bg: [254, 226, 226], text: [153, 27, 27] },
  EXPIRED:  { bg: [255, 237, 213], text: [154, 52, 18] },
};

// Navy-and-slate professional design: left logo/company block, right estimate
// meta + status badge, boxed "Estimate For", full-width dark table header,
// filled Total Amount bar. Colors are driven by the template's own colorScheme.
export const generateEstimatePDF = (estimate: Estimate) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  const colorScheme = estimate.template?.colorScheme;
  const navy:  [number, number, number] = colorScheme?.primary    ? hexToRgb(colorScheme.primary)    : [30, 58, 95];
  const text:  [number, number, number] = colorScheme?.text       ? hexToRgb(colorScheme.text)       : [31, 41, 55];
  const muted: [number, number, number] = colorScheme?.secondary  ? hexToRgb(colorScheme.secondary)  : [100, 116, 139];
  const boxBg: [number, number, number] = colorScheme?.background ? hexToRgb(colorScheme.background) : [248, 250, 252];

  const companyName = estimate.template?.templateCompanyName || estimate.company?.name || 'Your Company';

  // ── HEADER: logo + company name (left), ESTIMATE title + badge + meta (right) ──
  const logoSize = 24;
  const logoX = margin;
  const logoY = 10;
  let textX = margin;

  const logoSource = estimate.template?.templateLogo || estimate.company?.logo;
  const logoBase64 = estimate.template?.logoBase64;
  let hasLogo = false;

  if (logoBase64) {
    try {
      const base64Data = logoBase64.startsWith('data:') ? logoBase64 : `data:image/png;base64,${logoBase64}`;
      doc.addImage(base64Data, 'PNG', logoX, logoY, logoSize, logoSize);
      hasLogo = true;
    } catch { /* skip */ }
  } else if (logoSource) {
    try {
      const url = logoSource.startsWith('http')
        ? logoSource
        : `${window.location.origin.replace(':3000', ':5002')}/${logoSource}`;
      doc.addImage(url, 'PNG', logoX, logoY, logoSize, logoSize);
      hasLogo = true;
    } catch { /* skip */ }
  }
  if (hasLogo) textX = logoX + logoSize + 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...navy);
  const nameLines = doc.splitTextToSize(companyName.toUpperCase(), pageWidth - textX - 75);
  let nameY = logoY + 8;
  nameLines.forEach((line: string) => {
    doc.text(line, textX, nameY);
    nameY += 6;
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.setTextColor(...navy);
  doc.text('ESTIMATE', pageWidth - margin, 22, { align: 'right' });
  const titleWidth = doc.getTextWidth('ESTIMATE');

  // Status badge, to the right of the title
  const statusText = estimate.status.replace('_', ' ');
  const badge = STATUS_BADGE[estimate.status] || STATUS_BADGE.DRAFT;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const badgeTextW = doc.getTextWidth(statusText);
  const badgeW = badgeTextW + 8;
  const badgeH = 6;
  const badgeX = pageWidth - margin - titleWidth - 6 - badgeW;
  const badgeY = 22 - 8.5 - badgeH / 2;
  doc.setFillColor(...badge.bg);
  doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 1.5, 1.5, 'F');
  doc.setTextColor(...badge.text);
  doc.text(statusText, badgeX + badgeW / 2, badgeY + badgeH / 2 + 2.8, { align: 'center' });

  const metaRows = [
    { label: 'Estimate #:',  value: estimate.estimateNumber },
    { label: 'Issue Date:',  value: formatDate(estimate.issueDate) },
    { label: 'Valid Until:', value: formatDate(estimate.validUntil) },
  ];
  let metaY = 30;
  doc.setFontSize(9);
  metaRows.forEach((row) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...muted);
    doc.text(row.label, pageWidth - margin - 42, metaY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...text);
    doc.text(row.value, pageWidth - margin, metaY, { align: 'right' });
    metaY += 6;
  });

  let yPos = Math.max(nameY, metaY, logoY + logoSize) + 6;
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.6);
  doc.line(margin, yPos, pageWidth - margin, yPos);

  // ── ESTIMATE FOR box ─────────────────────────────────────────────────────────
  yPos += 8;
  const barW = 3;
  const boxW = pageWidth - margin * 2;
  const contentW = boxW - barW - 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const addrParts: string[] = [];
  if (estimate.customer?.address) addrParts.push(estimate.customer.address);
  const locParts = [
    estimate.customer?.city,
    estimate.customer?.state,
    estimate.customer?.postalCode,
    estimate.customer?.country,
  ].filter(Boolean);
  if (locParts.length) addrParts.push(locParts.join(', '));
  const addrLines = addrParts.length ? doc.splitTextToSize(addrParts.join(', '), contentW) : [];
  const hasContact = Boolean(estimate.customer?.email || estimate.customer?.phone);

  const boxH = 8 + 6 + addrLines.length * 4.5 + (hasContact ? 4.5 : 0) + 5;

  doc.setFillColor(...boxBg);
  doc.rect(margin, yPos, boxW, boxH, 'F');
  doc.setFillColor(...navy);
  doc.rect(margin, yPos, barW, boxH, 'F');

  const contentX = margin + barW + 7;
  let boxY = yPos + 7;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text('ESTIMATE FOR', contentX, boxY);
  boxY += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...text);
  doc.text(estimate.customer?.name || 'Customer', contentX, boxY);
  boxY += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...muted);
  if (addrLines.length) {
    doc.text(addrLines, contentX, boxY);
    boxY += addrLines.length * 4.5;
  }
  if (hasContact) {
    const contact = [estimate.customer?.email, estimate.customer?.phone].filter(Boolean).join(' | ');
    doc.text(contact, contentX, boxY);
  }

  yPos += boxH + 8;

  // ── ITEMS TABLE (no tax column) ─────────────────────────────────────────────
  const tableLeft  = margin;
  const tableRight = pageWidth - margin;
  const tableW     = tableRight - tableLeft;
  const headerH    = 10;
  const colW = { desc: 95, qty: 22, rate: 30, amount: 33 };

  const drawTableHeader = (y: number) => {
    doc.setFillColor(...navy);
    doc.rect(tableLeft, y, tableW, headerH, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(255, 255, 255);
    doc.text('DESCRIPTION', tableLeft + 3, y + 6.5);
    let hx = tableLeft + colW.desc;
    doc.text('QTY', hx + 3, y + 6.5);
    hx += colW.qty;
    doc.text('RATE', hx + 3, y + 6.5);
    doc.text('AMOUNT', tableRight - 3, y + 6.5, { align: 'right' });
  };

  drawTableHeader(yPos);
  yPos += headerH;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  estimate.items?.forEach((item) => {
    const descLinesPreview = doc.splitTextToSize(item.description, colW.desc - 5);
    const rowH = Math.max(12, descLinesPreview.length * 5 + 6);

    if (yPos + rowH > pageHeight - 65) {
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(tableLeft, yPos, tableRight, yPos);

      doc.addPage();
      yPos = 20;
      drawTableHeader(yPos);
      yPos += headerH;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
    }

    const rowStartY = yPos + 5;
    const descLines = doc.splitTextToSize(item.description, colW.desc - 5);
    doc.setTextColor(...text);
    doc.text(descLines, tableLeft + 3, rowStartY);
    const descH = descLines.length * 5;

    let cx = tableLeft + colW.desc + 3;
    doc.text(item.quantity.toString(), cx, rowStartY);
    cx += colW.qty;
    doc.text(formatCurrency(item.rate), cx, rowStartY);
    doc.text(formatCurrency(item.amount + (item.taxAmount || 0)), tableRight - 3, rowStartY, { align: 'right' });

    yPos += Math.max(12, descH + 6);

    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(tableLeft, yPos, tableRight, yPos);
  });

  // ── TOTALS + TOTAL AMOUNT bar ─────────────────────────────────────────────────
  yPos += 8;
  const summaryLeft = pageWidth - margin - 95;
  const summaryRight = pageWidth - margin;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...muted);
  doc.text('Subtotal', summaryLeft, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...text);
  doc.text(formatCurrency(estimate.subtotal), summaryRight, yPos, { align: 'right' });

  yPos += 6.5;
  const taxLabel = estimate.taxAmount === 0 ? 'Tax (0%)' : 'Tax';
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...muted);
  doc.text(taxLabel, summaryLeft, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...text);
  doc.text(formatCurrency(estimate.taxAmount), summaryRight, yPos, { align: 'right' });

  yPos += 6;
  const barH = 11;
  doc.setFillColor(...navy);
  doc.rect(summaryLeft, yPos, summaryRight - summaryLeft, barH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(255, 255, 255);
  doc.text('Total Amount', summaryLeft + 4, yPos + barH / 2 + 3.5);
  doc.text(formatCurrency(estimate.total), summaryRight - 4, yPos + barH / 2 + 3.5, { align: 'right' });

  // ── NOTES & TERMS ────────────────────────────────────────────────────────────
  yPos += barH + 10;

  if (yPos > pageHeight - 50) {
    doc.addPage();
    yPos = 20;
  }

  const notesText = estimate.notes
    ? estimate.notes
    : 'Thank you for considering our estimate! Please review the scope of work and pricing above. Feel free to reach out if you have any questions or require modifications.';

  const notesBoxW = pageWidth - margin * 2;
  const notesPad = 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const notesLines = doc.splitTextToSize(notesText, notesBoxW - notesPad * 2);
  const notesBoxH = 10 + notesLines.length * 4.5 + 6;

  doc.setFillColor(...boxBg);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.rect(margin, yPos, notesBoxW, notesBoxH, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...text);
  doc.text('NOTES & TERMS', margin + notesPad, yPos + 8);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...muted);
  doc.text(notesLines, margin + notesPad, yPos + 14.5);

  // ── FOOTER ────────────────────────────────────────────────────────────────────
  const footerContactParts: string[] = [];
  if (estimate.company?.city || estimate.company?.country) {
    footerContactParts.push([estimate.company?.city, estimate.company?.country].filter(Boolean).join(', '));
  }
  if (estimate.company?.address) footerContactParts.push(estimate.company.address);
  const footerContactLine = footerContactParts.join('  |  ');
  const footerReachLine = [estimate.company?.phone, estimate.company?.email].filter(Boolean).join('  |  ');

  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 22, pageWidth - margin, pageHeight - 22);

    let footY = pageHeight - 17;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...text);
    doc.text(companyName, pageWidth / 2, footY, { align: 'center' });
    footY += 4.5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    if (footerContactLine) {
      doc.text(footerContactLine, pageWidth / 2, footY, { align: 'center' });
      footY += 4;
    }
    if (footerReachLine) {
      doc.text(footerReachLine, pageWidth / 2, footY, { align: 'center' });
    }
  }

  const fileName = `Estimate_${estimate.estimateNumber}_${estimate.customer?.name || 'Customer'}.pdf`;
  doc.save(fileName);
};
